import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Modal } from 'antd'
import { recipientsApi } from '@/services/recipients/api'
import { isHandledAuthError } from '@/services/client'
import type {
  RecipientBankInfoCsvImportError,
  RecipientBankInfoCsvImportResponse,
} from '@/services/recipients/types'
import { downloadCsvString, toCsvString } from '@/utils/download'
import { useToast } from '@/widgets/common'
import { DemoSelect } from '@/widgets/demo/ListPrimitives'
import { demoButtonClass } from '@/widgets/demo/listHelpers'

type ImportStep = 'select' | 'mapping' | 'preview' | 'uploading' | 'result'
type MappingDraft = Record<number, string>

const FIELD_HOUSEHOLD_IDENT_CODE = 'householdIdentCode'
const FIELD_BANK_CODE = 'bankCode'
const FIELD_BANK_BRANCH_CODE = 'bankBranchCode'
const FIELD_BANK_ACCOUNT_TYPE = 'bankAccountType'
const FIELD_BANK_ACCOUNT_NUMBER = 'bankAccountNumber'
const FIELD_BANK_ACCOUNT_HOLDER_KANA = 'bankAccountHolderKana'

const BANK_INFO_FIELD_DEFS = [
  { key: FIELD_HOUSEHOLD_IDENT_CODE, label: '世帯識別符号' },
  { key: FIELD_BANK_CODE, label: '銀行番号' },
  { key: FIELD_BANK_BRANCH_CODE, label: '支店番号' },
  { key: FIELD_BANK_ACCOUNT_TYPE, label: '預金種目' },
  { key: FIELD_BANK_ACCOUNT_NUMBER, label: '口座番号' },
  { key: FIELD_BANK_ACCOUNT_HOLDER_KANA, label: '口座名義' },
] as const

const FIELD_LABEL_MAP: Map<string, string> = new Map(BANK_INFO_FIELD_DEFS.map((field) => [field.key, field.label]))
const REQUIRED_FIELD_KEYS: string[] = BANK_INFO_FIELD_DEFS.map((field) => field.key)
const HEADER_FIELD_LOOKUP = new Map<string, string>()
const REPLACEMENT_CHAR = '\uFFFD'

BANK_INFO_FIELD_DEFS.forEach((field) => {
  registerHeaderAlias(field.key, field.key)
  registerHeaderAlias(field.label, field.key)
})
registerHeaderAlias('household_ident_code', FIELD_HOUSEHOLD_IDENT_CODE)
registerHeaderAlias('世帯識別コード', FIELD_HOUSEHOLD_IDENT_CODE)
registerHeaderAlias('世帯番号', FIELD_HOUSEHOLD_IDENT_CODE)
registerHeaderAlias('f1', FIELD_HOUSEHOLD_IDENT_CODE)
registerHeaderAlias('bank_code', FIELD_BANK_CODE)
registerHeaderAlias('銀行コード', FIELD_BANK_CODE)
registerHeaderAlias('金融機関コード', FIELD_BANK_CODE)
registerHeaderAlias('f77', FIELD_BANK_CODE)
registerHeaderAlias('bank_branch_code', FIELD_BANK_BRANCH_CODE)
registerHeaderAlias('支店コード', FIELD_BANK_BRANCH_CODE)
registerHeaderAlias('f78', FIELD_BANK_BRANCH_CODE)
registerHeaderAlias('bank_account_type', FIELD_BANK_ACCOUNT_TYPE)
registerHeaderAlias('口座種別', FIELD_BANK_ACCOUNT_TYPE)
registerHeaderAlias('f79', FIELD_BANK_ACCOUNT_TYPE)
registerHeaderAlias('bank_account_number', FIELD_BANK_ACCOUNT_NUMBER)
registerHeaderAlias('f80', FIELD_BANK_ACCOUNT_NUMBER)
registerHeaderAlias('bank_account_holder_kana', FIELD_BANK_ACCOUNT_HOLDER_KANA)
registerHeaderAlias('口座名義カナ', FIELD_BANK_ACCOUNT_HOLDER_KANA)
registerHeaderAlias('f81', FIELD_BANK_ACCOUNT_HOLDER_KANA)

interface RecipientBankInfoCsvImportDialogProps {
  open: boolean
  onClose: () => void
  onImported: () => void
}

function readFileAsRows(file: File): Promise<string[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (event) => {
      const buffer = event.target?.result
      if (!(buffer instanceof ArrayBuffer)) {
        reject(new Error('CSVファイルの読み込みに失敗しました。'))
        return
      }

      try {
        const text = decodeCsvText(buffer)
        resolve(
          text
            .split(/\r?\n/)
            .filter((line) => line.trim().length > 0)
            .map(parseCsvLine),
        )
      } catch (error) {
        reject(error)
      }
    }

    reader.onerror = () => reject(new Error('CSVファイルの読み込みに失敗しました。'))
    reader.readAsArrayBuffer(file)
  })
}

function decodeCsvText(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  if (hasUtf8Bom(bytes)) {
    try {
      return decodeWithoutReplacement(buffer, 'utf-8')
    } catch {
      throw new Error('CSVファイルの文字コードを判定できません。UTF-8またはShift_JISで保存してください。')
    }
  }

  try {
    return decodeWithoutReplacement(buffer, 'utf-8')
  } catch {
    return decodeWithoutReplacement(buffer, 'shift_jis')
  }
}

function decodeWithoutReplacement(buffer: ArrayBuffer, encoding: 'utf-8' | 'shift_jis'): string {
  try {
    const text = new TextDecoder(encoding, { fatal: true }).decode(buffer)
    if (text.includes(REPLACEMENT_CHAR)) {
      throw new Error('replacement character detected')
    }
    return text
  } catch {
    if (encoding === 'shift_jis') {
      throw new Error('CSVファイルの文字コードを判定できません。UTF-8またはShift_JISで保存してください。')
    }
    throw new Error('UTF-8 decode failed')
  }
}

function hasUtf8Bom(bytes: Uint8Array): boolean {
  return bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf
}

function parseCsvLine(line: string): string[] {
  const fields: string[] = []
  let index = 0

  while (index <= line.length) {
    if (index === line.length) {
      fields.push('')
      break
    }

    if (line[index] === '"') {
      let value = ''
      index += 1
      while (index < line.length) {
        if (line[index] === '"') {
          if (line[index + 1] === '"') {
            value += '"'
            index += 2
            continue
          }
          index += 1
          break
        }
        value += line[index]
        index += 1
      }
      fields.push(value)
      if (line[index] === ',') index += 1
      continue
    }

    const commaIndex = line.indexOf(',', index)
    if (commaIndex === -1) {
      fields.push(line.slice(index))
      break
    }
    fields.push(line.slice(index, commaIndex))
    index = commaIndex + 1
  }

  return fields
}

function normalizeHeaderToken(value: string | undefined): string {
  return (value ?? '').replace(/^\uFEFF/, '').trim().toLowerCase()
}

function compactHeaderToken(value: string): string {
  return value.replace(/[\s\u3000]+/g, '')
}

function registerHeaderAlias(alias: string, fieldKey: string): void {
  const normalized = normalizeHeaderToken(alias)
  if (!normalized) return
  HEADER_FIELD_LOOKUP.set(normalized, fieldKey)
  HEADER_FIELD_LOOKUP.set(compactHeaderToken(normalized), fieldKey)
}

function resolveHeaderFieldKey(value: string | undefined): string | null {
  const normalized = normalizeHeaderToken(value)
  if (!normalized) return null
  return HEADER_FIELD_LOOKUP.get(normalized) ?? HEADER_FIELD_LOOKUP.get(compactHeaderToken(normalized)) ?? null
}

function createHeaderMappingDraft(headerRow: string[], columnCount: number): MappingDraft | null {
  const draft: MappingDraft = {}
  const usedTargets = new Set<string>()
  let nonBlankCount = 0

  for (let index = 0; index < columnCount; index += 1) {
    const value = headerRow[index]
    if (value == null || value.trim() === '') continue
    nonBlankCount += 1

    const target = resolveHeaderFieldKey(value)
    if (!target) continue
    if (usedTargets.has(target)) return null

    usedTargets.add(target)
    draft[index] = target
  }

  const matchedCount = Object.keys(draft).length
  const minimumMatches = Math.min(3, nonBlankCount)
  if (nonBlankCount === 0 || matchedCount < minimumMatches || matchedCount * 2 < nonBlankCount) {
    return null
  }
  return draft
}

function createDefaultMappingDraft(columnCount: number): MappingDraft {
  return Object.fromEntries(
    Array.from({ length: columnCount }, (_, index) => [
      index,
      index < BANK_INFO_FIELD_DEFS.length ? BANK_INFO_FIELD_DEFS[index].key : '',
    ]),
  ) as MappingDraft
}

function validateMappingDraft(draft: MappingDraft, columnCount: number): string | null {
  const usedTargets = new Map<string, number>()
  for (let index = 0; index < columnCount; index += 1) {
    const target = draft[index]
    if (!target) continue
    const duplicatedColumnIndex = usedTargets.get(target)
    if (duplicatedColumnIndex != null) {
      const fieldLabel = FIELD_LABEL_MAP.get(target) ?? target
      return `第 ${duplicatedColumnIndex + 1} 列と第 ${index + 1} 列が「${fieldLabel}」に重複してマッピングされています。`
    }
    usedTargets.set(target, index)
  }

  const missingFields = REQUIRED_FIELD_KEYS.filter((fieldKey) => !usedTargets.has(fieldKey))
  if (missingFields.length > 0) {
    return `必須項目が不足しています: ${missingFields.map((fieldKey) => FIELD_LABEL_MAP.get(fieldKey) ?? fieldKey).join('、')}`
  }
  return null
}

function fieldLabel(fieldKey: string): string {
  return FIELD_LABEL_MAP.get(fieldKey) ?? fieldKey
}

function normalizeRows(rows: string[][], columnCount: number): string[][] {
  return rows.map((row) => Array.from({ length: columnCount }, (_, index) => row[index] ?? ''))
}

const emptyResult: RecipientBankInfoCsvImportResponse = {
  success: false,
  updatedHouseholdCount: 0,
  updatedRecipientRowCount: 0,
  errors: [],
}

const RecipientBankInfoCsvImportDialog = ({
  open,
  onClose,
  onImported,
}: RecipientBankInfoCsvImportDialogProps) => {
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [step, setStep] = useState<ImportStep>('select')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [columnCount, setColumnCount] = useState(0)
  const [mappingDraft, setMappingDraft] = useState<MappingDraft>({})
  const [mappingError, setMappingError] = useState<string | null>(null)
  const [firstRow, setFirstRow] = useState<string[]>([])
  const [previewRows, setPreviewRows] = useState<string[][]>([])
  const [totalRowCount, setTotalRowCount] = useState(0)
  const [result, setResult] = useState<RecipientBankInfoCsvImportResponse>(emptyResult)

  const previewColumns = useMemo(() => {
    const columns: { index: number; fieldKey: string }[] = []
    for (let index = 0; index < columnCount; index += 1) {
      const fieldKey = mappingDraft[index]
      if (fieldKey) {
        columns.push({ index, fieldKey })
      }
    }
    return columns
  }, [columnCount, mappingDraft])

  const reset = useCallback(() => {
    setStep('select')
    setSelectedFile(null)
    setColumnCount(0)
    setMappingDraft({})
    setMappingError(null)
    setFirstRow([])
    setPreviewRows([])
    setTotalRowCount(0)
    setResult(emptyResult)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const closeDialog = useCallback(() => {
    reset()
    onClose()
  }, [onClose, reset])

  const beginFileFlow = useCallback(async (file: File) => {
    if (!/\.csv$/i.test(file.name)) {
      Modal.error({ content: 'CSVファイル（.csv）のみ取込できます。' })
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }
    try {
      const rows = await readFileAsRows(file)
      if (rows.length === 0) {
        Modal.error({ content: '選択されたCSVファイルが空です。' })
        return
      }

      const rawColumnCount = rows.reduce((max, row) => Math.max(max, row.length), 0)
      const nextColumnCount = Math.max(rawColumnCount, BANK_INFO_FIELD_DEFS.length)
      const headerMapping = createHeaderMappingDraft(rows[0] ?? [], nextColumnCount)
      const dataRows = headerMapping ? rows.slice(1) : rows
      if (dataRows.length === 0) {
        Modal.error({ content: 'CSVファイルにデータ行がありません。' })
        return
      }

      setSelectedFile(file)
      setColumnCount(nextColumnCount)
      setMappingDraft(headerMapping ?? createDefaultMappingDraft(nextColumnCount))
      setMappingError(null)
      setFirstRow((rows[0] ?? []).slice(0, nextColumnCount))
      setPreviewRows(normalizeRows(dataRows.slice(0, 5), nextColumnCount))
      setTotalRowCount(dataRows.length)
      setResult(emptyResult)
      setStep('mapping')
    } catch (error) {
      Modal.error({ content: error instanceof Error ? error.message : 'CSVファイルの読み込みに失敗しました。' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast])

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    await beginFileFlow(file)
  }

  const confirmMapping = () => {
    const validationMessage = validateMappingDraft(mappingDraft, columnCount)
    if (validationMessage) {
      setMappingError(validationMessage)
      return
    }
    setMappingError(null)
    setStep('preview')
  }

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return
    setStep('uploading')
    try {
      const { data } = await recipientsApi.uploadBankInfoCsv(selectedFile, JSON.stringify(mappingDraft))
      setResult(data)
      setStep('result')
      if (data.success) {
        toast.success('口座情報CSV取込が完了しました。')
        onImported()
      } else {
        toast.warning('CSVにエラーがあります。修正してから再度取込してください。')
      }
    } catch (error) {
      if (isHandledAuthError(error)) {
        setStep('preview')
        return
      }
      setStep('preview')
    }
  }, [mappingDraft, onImported, selectedFile, toast])

  const downloadErrorCsv = () => {
    if (result.errors.length === 0) return
    const rows = result.errors.map((row) => [
      row.rowNumber ?? '',
      row.householdIdentCode ?? '',
      row.field ? fieldLabel(row.field) : '',
      row.message,
    ])
    downloadCsvString(
      toCsvString(['行番号', '世帯識別符号', '項目', 'エラー内容'], rows),
      `口座情報CSV取込_エラー_${new Date().toISOString().slice(0, 10)}.csv`,
    )
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">口座情報CSV取込</h2>
          </div>
          <button
            type="button"
            onClick={closeDialog}
            className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            aria-label="閉じる"
          >
            ×
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(event) => void handleFileChange(event)}
        />

        <div className="max-h-[calc(92vh-76px)] overflow-auto px-6 py-5">
          {step === 'select' && (
            <div className="rounded-lg border-2 border-dashed border-gray-300 p-10 text-center">
              <p className="text-base font-medium text-gray-700">CSVファイルを選択してください。</p>
              <p className="mt-2 text-sm text-gray-500">
                必須列: 世帯識別符号、銀行番号、支店番号、預金種目、口座番号、口座名義
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`${demoButtonClass.primary} mt-6`}
              >
                CSV選択
              </button>
            </div>
          )}

          {step === 'mapping' && selectedFile && (
            <div>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">マッピング確認</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    CSV列がどの項目に対応するか確認してください。
                  </p>
                </div>
                <span className="rounded bg-gray-100 px-3 py-1 text-sm text-gray-700">{selectedFile.name}</span>
              </div>

              {mappingError ? (
                <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {mappingError}
                </div>
              ) : null}

              <div className="max-h-[56vh] overflow-auto rounded-lg border border-gray-200">
                <table className="w-full min-w-[48rem] text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">列</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">CSV 1行目</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">取込項目</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {Array.from({ length: columnCount }, (_, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                        <td className="max-w-[18rem] truncate px-4 py-3 text-gray-900" title={firstRow[index] ?? ''}>
                          {firstRow[index] || '-'}
                        </td>
                        <td className="px-4 py-3">
                          <DemoSelect
                            value={mappingDraft[index] ?? ''}
                            onChange={(event) => {
                              setMappingDraft((current) => ({ ...current, [index]: event.target.value }))
                              setMappingError(null)
                            }}
                            className="w-full pl-3 pr-9"
                          >
                            <option value="">- 取り込まない -</option>
                            {BANK_INFO_FIELD_DEFS.map((field) => (
                              <option key={field.key} value={field.key}>
                                {field.label}
                              </option>
                            ))}
                          </DemoSelect>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-5 flex justify-end gap-3">
                <button type="button" onClick={reset} className={demoButtonClass.outline}>
                  キャンセル
                </button>
                <button type="button" onClick={confirmMapping} className={demoButtonClass.primary}>
                  プレビューへ
                </button>
              </div>
            </div>
          )}

          {step === 'preview' && selectedFile && (
            <div>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">データ確認</h3>
                  <p className="mt-1 text-sm text-gray-500">先頭5件のみ表示しています。</p>
                </div>
                <div className="rounded bg-blue-50 px-4 py-2 text-sm text-blue-800">
                  取込対象 {totalRowCount.toLocaleString()} 件
                </div>
              </div>

              <div className="max-h-[56vh] overflow-auto rounded-lg border border-gray-200">
                <table className="w-full min-w-max text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-700">No.</th>
                      {previewColumns.map(({ index, fieldKey }) => (
                        <th key={`${index}-${fieldKey}`} className="px-3 py-2 text-left font-medium text-gray-700">
                          {fieldLabel(fieldKey)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {previewRows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        <td className="px-3 py-2 text-gray-500">{rowIndex + 1}</td>
                        {previewColumns.map(({ index }) => (
                          <td key={`${rowIndex}-${index}`} className="max-w-[12rem] truncate px-3 py-2" title={row[index] ?? ''}>
                            {row[index] ?? ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-5 flex justify-end gap-3">
                <button type="button" onClick={() => setStep('mapping')} className={demoButtonClass.outline}>
                  戻る
                </button>
                <button type="button" onClick={() => void handleUpload()} className={demoButtonClass.primary}>
                  取込実行
                </button>
              </div>
            </div>
          )}

          {step === 'uploading' && (
            <div className="py-14 text-center">
              <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-blue-100 border-t-blue-600" />
              <p className="mt-4 text-sm text-gray-600">取込処理中...</p>
            </div>
          )}

          {step === 'result' && (
            <div>
              <div className={result.success ? 'rounded-lg border border-green-200 bg-green-50 p-5' : 'rounded-lg border border-red-200 bg-red-50 p-5'}>
                <h3 className={result.success ? 'font-bold text-green-900' : 'font-bold text-red-900'}>
                  {result.success ? '取込が完了しました。' : 'CSVにエラーがあります。'}
                </h3>
                <div className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
                  <div className="rounded bg-white/80 p-3">
                    <p className="text-gray-500">更新世帯件数</p>
                    <p className="text-xl font-bold text-gray-900">{result.updatedHouseholdCount.toLocaleString()}</p>
                  </div>
                  <div className="rounded bg-white/80 p-3">
                    <p className="text-gray-500">更新明細件数</p>
                    <p className="text-xl font-bold text-gray-900">{result.updatedRecipientRowCount.toLocaleString()}</p>
                  </div>
                  <div className="rounded bg-white/80 p-3">
                    <p className="text-gray-500">エラー</p>
                    <p className="text-xl font-bold text-gray-900">{result.errors.length.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {result.errors.length > 0 ? (
                <div className="mt-5">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h4 className="font-semibold text-gray-900">エラー一覧</h4>
                    <button type="button" onClick={downloadErrorCsv} className={demoButtonClass.outline}>
                      エラーCSV出力
                    </button>
                  </div>
                  <div className="max-h-[42vh] overflow-auto rounded-lg border border-gray-200">
                    <table className="w-full min-w-[48rem] text-sm">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">行番号</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">世帯識別符号</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">項目</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-700">エラー内容</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.errors.map((row: RecipientBankInfoCsvImportError, index: number) => (
                          <tr key={`${row.rowNumber ?? 'mapping'}-${row.field ?? 'field'}-${index}`}>
                            <td className="px-3 py-2">{row.rowNumber ?? '-'}</td>
                            <td className="px-3 py-2">{row.householdIdentCode ?? '-'}</td>
                            <td className="px-3 py-2">{row.field ? fieldLabel(row.field) : '-'}</td>
                            <td className="px-3 py-2 text-red-800">{row.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}

              <div className="mt-5 flex justify-end gap-3">
                <button type="button" onClick={reset} className={demoButtonClass.outline}>
                  続けて取込
                </button>
                <button type="button" onClick={closeDialog} className={demoButtonClass.primary}>
                  閉じる
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default RecipientBankInfoCsvImportDialog
